/**
 * <poster-frame-picker> til Prestige 11
 *
 * Gør fire ting:
 *  1. Viser kun de passepartouter/rammer, der passer til den valgte størrelse
 *  2. Bygger 3D Empire/artar-URL'en og skifter forhåndsvisningen i galleriet
 *  3. Lægger passepartout/ramme oveni prisen (hoved-pris og sticky-bar)
 *  4. Sender passepartout og ramme med som ekstra linjer, når der lægges i kurv
 *
 * Prestige 11 erstatter blokken ved hvert variantskift (product-rerender), så
 * elementet gen-initialiserer sig selv i connectedCallback, og valget gemmes i
 * et modul-niveau Map, så farvevalget overlever størrelsesskift.
 *
 * Sprog: Størrelser matches ud fra selve målene ("70x100", "A4") og ikke ud fra
 * hele variantnavnet, så modulet virker ens på dansk og engelsk. Tekster som
 * "Ingen ramme" kommer fra sprogfilerne via data.labels.
 */

/* ------------------------------------------------------------------ */
/* Sproguafhængig fortolkning af variantnavne                          */
/* ------------------------------------------------------------------ */

const SIZE_PATTERN = /\bA\d\b|\d+(?:[.,]\d+)?\s*x\s*\d+(?:[.,]\d+)?/gi;
const ONLY_SIZE_PATTERN = /^(?:A\d|\d+(?:[.,]\d+)?\s*x\s*\d+(?:[.,]\d+)?)$/i;
const CANVAS_PATTERN = /lærred|canvas/i;
const ACRYLIC_PATTERN = /akryl|acrylic/i;

/** Alle mål i en tekst, normaliseret: "70 x 100" → "70X100", "a4" → "A4" */
function sizeTokens(text) {
  return [...String(text ?? '').matchAll(SIZE_PATTERN)].map((match) =>
    match[0].replace(/\s+/g, '').replace(/,/g, '.').toUpperCase()
  );
}

/**
 * Plakatens størrelse:
 *  - "plain"  = kun et mål (fx "70x100") → passepartout og ramme kan vælges
 *  - "canvas" = lærredsprint → kun lærredsrammer
 *  - "none"   = alt andet (fx "kun plakat", akustikprint) → ingen tilvalg
 */
function parsePosterSize(title) {
  const text = String(title ?? '').trim();
  const size = sizeTokens(text)[0] ?? null;

  if (ONLY_SIZE_PATTERN.test(text)) return { size, kind: 'plain' };
  if (CANVAS_PATTERN.test(text)) return { size, kind: 'canvas' };
  return { size, kind: 'none' };
}

/** Rammens størrelse: akryl og almindelige rammer er "plain", lærredsrammer "canvas" */
function parseFrameSize(title) {
  const text = String(title ?? '').trim();
  const size = sizeTokens(text)[0] ?? null;

  if (ACRYLIC_PATTERN.test(text) || ONLY_SIZE_PATTERN.test(text)) return { size, kind: 'plain' };
  if (CANVAS_PATTERN.test(text)) return { size, kind: 'canvas' };
  return { size, kind: 'none' };
}

/** Passepartout: første mål = plakatstørrelse, andet mål = ydermål (krævet ramme) */
function parsePassepartoutSize(title) {
  const [size = null, requiredFrame = null] = sizeTokens(title);
  return { size, requiredFrame };
}

class PosterFramePicker extends HTMLElement {
  static #states = new Map();

  #abort = null;
  #data = null;
  #form = null;
  #formId = null;
  #ppSelect = null;
  #frameSelect = null;
  #selectedPassepartout = null;
  #selectedFrame = null;
  #moneyFormat;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#data = JSON.parse(this.querySelector('[data-poster-data]').textContent);
    this.#data.variants.forEach((item) => Object.assign(item, parsePosterSize(item.title)));
    this.#data.passepartouts.forEach((item) => Object.assign(item, parsePassepartoutSize(item.title)));
    this.#data.frames.forEach((item) => Object.assign(item, parseFrameSize(item.title)));
    this.#formId = this.closest('product-rerender')?.getAttribute('observe-form');
    this.#form = this.#formId ? document.forms[this.#formId] : null;

    if (!this.#form) {
      return;
    }

    this.#ppSelect = this.querySelector('[data-role="passepartout"]');
    this.#frameSelect = this.querySelector('[data-role="frame"]');

    this.#ppSelect.addEventListener('change', () => this.#onChange('passepartout'), { signal });
    this.#frameSelect.addEventListener('change', () => this.#onChange('frame'), { signal });

    // Temaets egen ProductForm bruger new FormData(form) – "formdata"-eventet lader os ændre den
    this.#form.addEventListener('formdata', (event) => this.#onFormData(event.formData), { signal });

    // Sikkerhedsnet, hvis blokken ikke blev erstattet ved variantskift
    this.#form.addEventListener(
      'variant:change',
      () => {
        this.#render();

        // Temaet hopper selv til variantens eget billede og regner med, at billederne starter i position 1.
        // Vores forhåndsvisning ligger først i galleriet, så vi vender tilbage til den bagefter.
        setTimeout(() => {
          document.querySelector(`product-gallery[form="${this.#formId}"]`)?.carousel?.select(0, { instant: true });
        }, 0);
      },
      { signal }
    );

    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  get #state() {
    const key = String(this.#data.productId);

    if (!PosterFramePicker.#states.has(key)) {
      // Valget huskes pr. produkt-ID (én farve = ét produkt), så det virker på alle sprog
      PosterFramePicker.#states.set(key, { passepartoutProductId: null, frameProductId: null });
    }

    return PosterFramePicker.#states.get(key);
  }

  #currentVariant() {
    const id = Number(this.#form.elements['id']?.value);
    return this.#data.variants.find((variant) => variant.id === id) ?? null;
  }

  #onChange(kind) {
    const state = this.#state;

    if (kind === 'passepartout') {
      const selected = this.#data.passepartouts.find((item) => String(item.id) === this.#ppSelect.value);
      state.passepartoutProductId = selected ? selected.productId : null;
    } else {
      const selected = this.#data.frames.find((item) => String(item.id) === this.#frameSelect.value);
      state.frameProductId = selected ? selected.productId : null;
    }

    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  #render() {
    const variant = this.#currentVariant();

    if (!variant) {
      return;
    }

    const { passepartouts, frames, labels = {} } = this.#data;
    const state = this.#state;

    // Passepartout: kun til almindelige plakater, og kun i plakatens størrelse
    const passepartoutList =
      variant.kind === 'plain'
        ? passepartouts.filter((item) => item.available && item.size === variant.size)
        : [];
    this.#selectedPassepartout = passepartoutList.find((item) => item.productId === state.passepartoutProductId) ?? null;
    this.#fill(this.#ppSelect, passepartoutList, this.#selectedPassepartout, labels.noPassepartout ?? 'Ingen passepartout');

    // Ramme: med passepartout skal rammen passe til ydermålet, ellers til plakaten (lærred kun til lærred)
    let frameList = [];

    if (this.#selectedPassepartout) {
      frameList = frames.filter(
        (item) => item.available && item.kind === 'plain' && item.size === this.#selectedPassepartout.requiredFrame
      );
    } else if (variant.kind !== 'none') {
      frameList = frames.filter(
        (item) => item.available && item.kind === variant.kind && item.size === variant.size
      );
    }

    this.#selectedFrame = frameList.find((item) => item.productId === state.frameProductId) ?? null;
    this.#fill(this.#frameSelect, frameList, this.#selectedFrame, labels.noFrame ?? 'Ingen ramme');

    this.querySelector('[data-field="passepartout"]').hidden = passepartoutList.length === 0;
    this.querySelector('[data-field="frame"]').hidden = frameList.length === 0;

    this.#updateImage(variant);
    this.#updatePrice();

    // Sticky-baren erstattes af temaet EFTER vores blok, så vi opdaterer prisen igen bagefter
    setTimeout(() => this.#updatePrice(), 0);
  }

  #fill(select, list, selected, noneLabel) {
    select.replaceChildren(
      new Option(noneLabel, ''),
      ...list.map((item) => new Option(`${item.label} (+${this.#money(item.price)})`, String(item.id)))
    );
    select.value = selected ? String(selected.id) : '';

    // Den synlige boks (temaets .select) viser den valgte tekst; selecten selv er usynlig
    const display = select.closest('.poster-picker__control')?.querySelector('[data-selected-value]');

    if (display) {
      display.textContent = select.selectedOptions[0]?.text ?? '';
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3D Empire-billede                                                   */
  /* ------------------------------------------------------------------ */

  #updateImage(variant) {
    const frameId = this.#selectedFrame ? this.#selectedFrame.productId : 'null';
    const passepartoutId = this.#selectedPassepartout ? this.#selectedPassepartout.productId : 'null';

    // Stavemåden "passpartout" (med ét e) er den, artar-API'et forventer
    const url = `${this.#data.artarUrl}?product=${this.#data.productId}&variant=${variant.id}&frame=${frameId}&passpartout=${passepartoutId}`;

    document
      .querySelectorAll(`product-gallery[form="${this.#formId}"] [data-poster-preview], product-gallery[form="${this.#formId}"] [data-poster-preview-thumb]`)
      .forEach((image) => {
        if (image.getAttribute('src') === url) {
          return;
        }

        image.removeAttribute('srcset');

        // Zoom (PhotoSwipe) bruger billedets width/height – de skal matche det faktiske billede
        image.onload = () => {
          if (image.naturalWidth > 0) {
            image.setAttribute('width', image.naturalWidth);
            image.setAttribute('height', image.naturalHeight);
          }
        };

        image.src = url;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Pris                                                                */
  /* ------------------------------------------------------------------ */

  #updatePrice() {
    const variant = this.#currentVariant();

    if (!variant) {
      return;
    }

    const extras = (this.#selectedPassepartout?.price ?? 0) + (this.#selectedFrame?.price ?? 0);

    // Pris på "Tilføj til kurv"-knapperne (hovedknap og sticky-bjælke)
    document.querySelectorAll(`buy-buttons[form="${this.#formId}"] [data-atc-price]`).forEach((element) => {
      element.textContent = this.#formatMoney(variant.price + extras);
    });

    document.querySelectorAll(`product-rerender[observe-form="${this.#formId}"] price-list`).forEach((list) => {
      const salePrice = list.querySelector('sale-price');
      const compareAtPrice = list.querySelector('compare-at-price');

      if (salePrice) {
        this.#writePrice(salePrice, variant.price + extras);
      }

      if (compareAtPrice && variant.compareAt > 0) {
        this.#writePrice(compareAtPrice, variant.compareAt + extras);
      }
    });
  }

  #writePrice(element, cents) {
    const textNodes = [...element.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '');
    let target = element.querySelector('[data-poster-price]');

    textNodes.forEach((node) => node.remove());

    if (!target) {
      target = document.createElement('span');
      target.setAttribute('data-poster-price', '');
      element.append(target);
    }

    // Samme pengeformat som temaets egne priser (og som knappen)
    target.textContent = this.#formatMoney(cents);
  }

  /**
   * Formaterer et beløb præcis som temaets egen "money"-filter gør det.
   * Liquid sender et eksempel (123456 øre → fx "1.234,56 kr."), og formatet aflæses ud fra det,
   * så vi ikke selv skal gætte på valutategn, decimaler og skilletegn.
   */
  #formatMoney(cents) {
    if (this.#moneyFormat === undefined) {
      this.#moneyFormat = this.#parseMoneySample(this.#data.moneySample);
    }

    const format = this.#moneyFormat;

    if (!format) {
      return this.#money(cents);
    }

    const parts = (cents / 100).toFixed(format.decimals).split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
    const number = parts[1] ? whole + format.decimal + parts[1] : whole;

    return format.prefix + number + format.suffix;
  }

  #parseMoneySample(sample) {
    const match = String(sample || '').match(/^(\D*?)(\d[\d.,'\u2019\u00a0\u202f ]*\d)(\D*)$/);

    if (!match) {
      return null;
    }

    const [, prefix, numberPart, suffix] = match;
    const decimalMatch = numberPart.match(/([.,])(\d{2})$/);
    const decimal = decimalMatch ? decimalMatch[1] : '';
    const integerPart = decimal ? numberPart.slice(0, -3) : numberPart;
    const thousandsMatch = integerPart.match(/\D/);

    return {
      prefix,
      suffix,
      decimals: decimal ? 2 : 0,
      decimal,
      thousands: thousandsMatch ? thousandsMatch[0] : ''
    };
  }

  #money(cents, decimals = cents % 100 === 0 ? 0 : 2) {
    return new Intl.NumberFormat(document.documentElement.lang || 'da', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'DKK',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(cents / 100);
  }

  /* ------------------------------------------------------------------ */
  /* Kurv                                                                */
  /* ------------------------------------------------------------------ */

  /**
   * Skriver /cart/add.js-kaldet om til items[0]=plakat, items[1]=passepartout, items[2]=ramme.
   * Antal følger plakatens antal (samme som det gamle tema).
   */
  #onFormData(formData) {
    const extras = [this.#selectedPassepartout, this.#selectedFrame].filter(Boolean);
    const posterVariantId = formData.get('id');

    if (extras.length === 0 || !posterVariantId) {
      return;
    }

    const quantity = formData.get('quantity') || '1';

    // Når "items" bruges, skal de enkelte linjer ikke også ligge som top-level id/quantity
    formData.delete('id');
    formData.delete('quantity');

    [{ id: posterVariantId }, ...extras].forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, quantity);
    });
  }
}

if (!window.customElements.get('poster-frame-picker')) {
  window.customElements.define('poster-frame-picker', PosterFramePicker);
}